import React, { useMemo, useState } from "react";
import { StyleSheet, View, Text, Pressable, Dimensions } from "react-native";
import { InformationSquare } from "@assets/icons/InformationSquare";
import { useSelector } from "react-redux";
import { GlobalState } from "@redux";
import { useDevices } from "@hooks";
import { Spacing, Colors } from "@constants";
import { I18n } from "react-redux-i18n";
import { ModalTemplate } from '@atomic/templates';

// @ts-ignore
export const AllergensInfoModal: React.FC = React.memo(({
}) => {

    const [isMobile] = useDevices();
    const [showAllergenModal, setShowAllergenModal] = useState<boolean>(false);
    const brandTheme = useSelector(
      (state: GlobalState) => state.brandTheme.brandTheme
    );
    const styles = useMemo(() => _styles(brandTheme, isMobile), [brandTheme, isMobile]);

    const { 
      showAllergensInfo,
    } =  useSelector(
      (state: GlobalState) => ({
        showAllergensInfo: state.auth.userInfo?.showAllergensInfo,
      })
    );

    const onClose = () => {

      setShowAllergenModal(false);
    }

    return (
      <View style={styles.container}>
        { showAllergensInfo && (
          <Pressable style={styles.mainBtn} onPress={() => setShowAllergenModal(true)} nativeID="testeeeee">
            <View style={styles.iconTitleContainer}>
              <View style={styles.iconContainer}>
                <InformationSquare 
                  color={brandTheme.buttonActionColors?.textColor || Colors.foodiBlack} 
                  backgroundColor={brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault} 
                  size="30"
                />
              </View>
              <Text style={styles.title}>{I18n.t("allergensInfoModal.title")}</Text>
            </View>
          </Pressable>
        )}
        <ModalTemplate
            isOpen={showAllergenModal}
            handleClose={onClose}
            closeOnClickOutside={true}
            isCenter={true}
            isFullScreen={true}
            style={styles.modal}
            isMobile={isMobile}
            bodyStyle={styles.modalBody}
            showsVerticalScrollIndicator
        >
            {
              // important info 
            }
            <View style={styles.section}>
              <Text style={[styles.fontBold, styles.red, styles.italic]}>{I18n.t("allergensInfoModal.importantTitle")}</Text>
              <Text style={[styles.fontBold, styles.italic]}>{I18n.t("allergensInfoModal.importantDesc")}</Text>
              <Text style={[styles.fontBold, styles.italic]}>
                {I18n.t("allergensInfoModal.importantInfo-1")}
                <Text style={styles.red}>{I18n.t("allergensInfoModal.importantInfo-2")}</Text>
                {I18n.t("allergensInfoModal.importantInfo-3")}
              </Text>
            </View>
            {
              // allergens info 
            }
            <View style={styles.section}>
              <Text style={[styles.fontBold]}>{I18n.t("allergensInfoModal.allergensTitle")}</Text>
              <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.allergensDesc")}</Text>
              <Text style={[styles.fontBold, styles.red]}>{I18n.t("allergensInfoModal.allergensNote")}</Text>
              <View style={styles.bulletSection}>
                <Text style={styles.bulletChar}>{'\u2B24'}</Text>
                <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.allergensNote1")}</Text>
              </View>
              <View style={styles.bulletSection}>
                <Text style={styles.bulletChar}>{'\u2B24'}</Text>
                <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.allergensNote2")}</Text>
              </View>
              
              <Text style={[styles.fontBold]}>{I18n.t("allergensInfoModal.allergensNote3")}</Text>
            </View>
            {
              // main ingredients info 
            }
            <View style={styles.section}>
              <Text style={[styles.fontBold]}>{I18n.t("allergensInfoModal.mainIngredientesTitle")}</Text>
              <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.mainIngredientesInfo1")}</Text>
              <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.mainIngredientesInfo2")}</Text>
            </View>
            {
              // meat origins info 
            }
            <View>
              <Text style={[styles.fontBold]}>{I18n.t("allergensInfoModal.meatOriginsTitle")}</Text>
              <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.meatOriginsInfo1")}</Text>
              <Text style={[styles.fontNormal]}>{I18n.t("allergensInfoModal.meatOriginsInfo2")}</Text>
            </View>
        </ModalTemplate>
        
      </View>
    );
  });

const OPACITY_20 = '33'; // adding this to a hex color sets the opacity to 20%
const _styles = (brandTheme: any, isMobile: boolean) => 
  StyleSheet.create({
    container: {
      flexDirection: "row",
      marginTop: isMobile ? Spacing.S : 0,
      marginBottom: Spacing.M,
      justifyContent: "flex-end",
      width: "fit-content",
    },
    iconTitleContainer: {
      flexDirection: "row",
      alignItems: "center",
    },
    mainBtn: {
      flexDirection: "column",
      backgroundColor: (brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault)+OPACITY_20,
      padding: Spacing.S,
      borderRadius: 8,
      borderColor: brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault,
      borderWidth: 1.5,
    },
    title: {
      color: Colors.foodiBlack,
      fontFamily: "manrope",
      fontSize: isMobile ? 16 : 18
    },
    modal: {
      width: isMobile ? Dimensions.get('window').width : 'auto',
      maxHeight: isMobile ? Dimensions.get('window').height : "100%",
      flexGrow: 0,
    },
    modalBody: {
      bottom: isMobile ? 0 : "auto",
      maxWidth: isMobile ? Dimensions.get("window").width : "50%",
      justifyContent: isMobile ? "flex-end" : "center",
      maxHeight: "90%",
    },
    fontBold: {
      color: Colors.foodiBlack,
      fontFamily: "manrope-bold",
      fontSize: 16,
      paddingBottom: Spacing.S
    },
    fontNormal: {
      color: Colors.foodiBlack,
      fontFamily: "manrope",
      fontSize: 16,
      paddingBottom: Spacing.S
    },
    red: {
      color: Colors.redError
    },
    italic: {
      fontStyle: "italic",
    },
    section: {
      paddingBottom: Spacing.L,
    },
    bulletChar: {
      fontSize: 6,
      paddingBottom: Spacing.S,
      paddingRight: Spacing.S,
    },
    bulletSection: {
      flexDirection: "row",
      alignItems: "center",
      paddingLeft: Spacing.M
    },
    iconContainer: {
      marginRight: Spacing.S
    }
  });
